import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trophy, Calendar, Users, Clock, Search, Filter, RefreshCw } from 'lucide-react';
import { hackathonAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  maxTeams: number;
  currentTeams: number;
  status: string;
  prizes: Array<{ rank: number; description: string; value: string }>;
  problemStatements: Array<{
    title: string;
    description: string;
    category: string;
    difficulty: string;
  }>;
}

const HackathonList: React.FC = () => {
  const location = useLocation();
  const [hackathons, setHackathons] = useState<Hackathon[]>([]);
  const [filteredHackathons, setFilteredHackathons] = useState<Hackathon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchHackathons();
  }, []);

  // Check if we need to refresh after navigation
  useEffect(() => {
    if (location.state?.refresh) {
      fetchHackathons();
      // Clear the refresh state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    filterHackathons();
  }, [hackathons, searchTerm, statusFilter]);

  const fetchHackathons = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching hackathons...');
      const data = await hackathonAPI.getHackathons();
      console.log('Hackathons data:', data);
      
      // Handle the response format from backend
      if (Array.isArray(data)) {
        // Direct array response
        setHackathons(data);
      } else if (data && Array.isArray(data.hackathons)) {
        // Response with hackathons array and pagination
        setHackathons(data.hackathons);
      } else {
        console.error('API returned unexpected data format:', data);
        setHackathons([]);
        setError('Invalid data format received from server');
      }
    } catch (err: any) {
      console.error('Error details:', err);
      setError(err.response?.data?.message || 'Failed to fetch hackathons');
      console.error('Error fetching hackathons:', err);
      setHackathons([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const filterHackathons = () => {
    // Ensure hackathons is always an array
    if (!Array.isArray(hackathons)) {
      console.error('Hackathons is not an array:', hackathons);
      setFilteredHackathons([]);
      return;
    }

    let filtered = [...hackathons];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(hackathon =>
        hackathon.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hackathon.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(hackathon => hackathon.status === statusFilter);
    }

    setFilteredHackathons(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Registration Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} days left`;
    return `${hours} hours left`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registration_open':
        return 'text-green-600';
      case 'upcoming':
        return 'text-blue-600';
      case 'in_progress':
        return 'text-orange-600';
      case 'completed':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <Button onClick={fetchHackathons} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Hackathons</h1>
          </div>
          <Button
            onClick={fetchHackathons}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
        <p className="text-gray-600">Discover and participate in exciting hackathons</p>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search hackathons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="registration_open">Registration Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredHackathons.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {hackathons.length === 0 ? 'No hackathons available' : 'No hackathons match your filters'}
          </h3>
          <p className="text-gray-600">
            {hackathons.length === 0 
              ? 'Check back later for upcoming hackathons' 
              : 'Try adjusting your search or filters'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredHackathons.map((hackathon) => (
            <Card key={hackathon._id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-xl font-semibold text-gray-900 line-clamp-2">
                    {hackathon.title}
                  </CardTitle>
                  <StatusBadge status={hackathon.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 line-clamp-3">
                  {hackathon.description}
                </p>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{formatDate(hackathon.startDate)} at {formatTime(hackathon.startDate)} - {formatDate(hackathon.endDate)} at {formatTime(hackathon.endDate)}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-2" />
                    <span>{hackathon.currentTeams}/{hackathon.maxTeams} teams</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-2" />
                    <span className={getStatusColor(hackathon.status)}>
                      {getTimeRemaining(hackathon.registrationDeadline)}
                    </span>
                  </div>
                </div>

                {hackathon.prizes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Prizes</h4>
                    <div className="space-y-1">
                      {hackathon.prizes.slice(0, 3).map((prize, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          {prize.rank === 1 ? '🥇' : prize.rank === 2 ? '🥈' : '🥉'} {prize.value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2">
                  <Link
                    to={`/hackathons/${hackathon._id}`}
                    className="block w-full"
                  >
                    <Button className="w-full">
                      View Details
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HackathonList; 